import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useApp } from '../../contexts/AppContext';
import { Button } from '../../components/ui';
import { TrustBadges } from '../../components/shared';
import { track, EVENTS } from '../../utils/analytics';
import type { UserType } from '../../types';

export default function QuizEntry() {
  const navigate = useNavigate();
  const { setFlow, setUserType } = useApp();
  const [selected, setSelected] = useState<UserType | null>(null);

  useEffect(() => {
    track(EVENTS.QUIZ_PAGE_VIEWED);
  }, []);

  function handleContinue() {
    if (!selected) return;
    track(EVENTS.QUIZ_STARTED, { userType: selected });
    setFlow('quiz');
    setUserType(selected);
    navigate(selected === 'BUSINESS' ? '/quiz/business' : '/quiz/consumer');
  }

  return (
    <>
    <Helmet>
      <title>Build Your Financial Map | Sequence</title>
      <meta name="description" content="Design your personalized financial automation map with AI. Set rules once, let your money flow automatically." />
    </Helmet>
    <div className="value-prop">
      <div className="value-prop__container">
        <div className="value-prop__header">
          <h1 className="value-prop__headline">
            Get Your Free Financial Blueprint in 60 Seconds
          </h1>
          <p className="value-prop__tagline">
            Answer a few quick questions and we'll show you exactly how to organize your money, customized for your situation.
          </p>
        </div>

        {/* Main CTAs - prominent and clear */}
        <div className="quiz-step__body" style={{ marginTop: 'var(--space-8)' }}>
          <div className="quiz-step__header">
            <h2 className="quiz-step__question" style={{ fontSize: 'var(--font-size-xl)' }}>
              Which best describes you?
            </h2>
          </div>

          <div className="entry-cards-grid">
            <button
              type="button"
              className={`entry-card${selected === 'INDIVIDUAL' ? ' entry-card--selected' : ''}`}
              onClick={() => setSelected('INDIVIDUAL')}
            >
              <span className="entry-card__indicator" />
              <span className="entry-card__icon">👤</span>
              <span className="entry-card__title">I'm focused on my personal finances</span>
              <span className="entry-card__description">Get my savings and spending on track</span>
            </button>

            <button
              type="button"
              className={`entry-card${selected === 'BUSINESS' ? ' entry-card--selected' : ''}`}
              onClick={() => setSelected('BUSINESS')}
            >
              <span className="entry-card__indicator" />
              <span className="entry-card__icon">🏢</span>
              <span className="entry-card__title">I run a business</span>
              <span className="entry-card__description">Manage cash flow and grow profit</span>
            </button>
          </div>

          <div style={{ width: '100%', maxWidth: '480px', margin: 'var(--space-6) auto 0' }}>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={!selected}
              onClick={handleContinue}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              }
              iconPosition="right"
            >
              Continue
            </Button>
          </div>
        </div>

        {/* Stats as sentence */}
        <p className="value-prop__subtitle" style={{ marginTop: 'var(--space-6)' }}>
          Takes just 1 minute - 100% free
        </p>

        <div style={{ marginTop: 'var(--space-6)', textAlign: 'center' }}>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-3)' }}>
            Already know what you need?
          </p>
          <Link to="/express" onClick={() => { track(EVENTS.EXPRESS_LINK_CLICKED); track(EVENTS.DESCRIBE_OWN_WORDS_CLICKED); }}>
            <Button variant="ghost" size="md">
              Describe it in your own words →
            </Button>
          </Link>
        </div>

        <div style={{ marginTop: 'var(--space-8)' }}>
          <TrustBadges />
        </div>
      </div>
    </div>
    </>
  );
}
